import { useEffect, useState } from 'react';

/**
 * [CF 2026-08-02] Shared sorting for every customer listing page.
 *
 * The values below are the SAME strings the iOS app uses (its `FilterType`
 * enum in `Filtergroup/FilteranotherViewController.swift`) and the same ones
 * the API's `sort` query param understands — so web, app and backend all speak
 * one vocabulary even though the web sorts client-side.
 *
 * Client-side is deliberate: these pages already fetch the whole category in
 * one call and filter/search locally, and the backend's own `sort` support is
 * uneven (taxi, towing and driving school have no sort switch at all, and the
 * `distance` case is an empty stub everywhere). Sorting the array we already
 * hold gives every category the full option set with no API change.
 */
export const SORT = {
  RECOMMENDED: '',
  A2Z: 'a2z',
  Z2A: 'z2a',
  NEW_TO_OLD: 'newToOld',
  OLD_TO_NEW: 'oldToNew',
  PRICE_LOW_HIGH: 'priceLowToHigh',
  PRICE_HIGH_LOW: 'priceHighToLow',
  DISTANCE: 'distance',
};

/** Options for the sort menu. `withPrice` only where rows carry a price. */
export function sortOptions({ isRTL = false, withPrice = false } = {}) {
  const L = (en, ar) => (isRTL ? ar : en);
  const opts = [
    // [CF 2026-08-02 v2] "Recommended" was to become a most-viewed ranking, but
    // the client dropped that for now: nearest-first IS the default ordering,
    // so Distance leads the menu and there is no Recommended row at all.
    { value: SORT.DISTANCE, label: L('Distance: Near - Far', 'المسافة: الأقرب') },
    { value: SORT.A2Z, label: L('A - Z', 'أ - ي') },
    { value: SORT.Z2A, label: L('Z - A', 'ي - أ') },
    { value: SORT.NEW_TO_OLD, label: L('Newest - Oldest', 'الأحدث - الأقدم') },
    { value: SORT.OLD_TO_NEW, label: L('Oldest - Newest', 'الأقدم - الأحدث') },
  ];
  if (withPrice) {
    opts.push({
      value: SORT.PRICE_LOW_HIGH,
      label: L('Price: Low - High', 'السعر: من الأقل'),
    });
    opts.push({
      value: SORT.PRICE_HIGH_LOW,
      label: L('Price: High - Low', 'السعر: من الأعلى'),
    });
  }
  return opts;
}

/** `gps` is stored as a JSON STRING on most rows and as an object on some. */
function readGps(row) {
  const raw = row?.gps;
  if (!raw) return null;
  let g = raw;
  if (typeof raw === 'string') {
    try {
      g = JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }
  const lat = parseFloat(g?.lat);
  const lng = parseFloat(g?.long ?? g?.lng);
  if (!isFinite(lat) || !isFinite(lng)) return null;
  return { lat, lng };
}

/** Great-circle distance in km. Only used for ordering, so precision is ample. */
function distanceKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.sqrt(s));
}

const num = (v) => {
  const n = parseFloat(v);
  return isFinite(n) ? n : null;
};

/**
 * Sorts IN PLACE on a copy the caller already made (every page does
 * `let list = [...allRows]` first), and always returns the list.
 *
 * Rows that can't participate in the chosen sort (no date, no price, no gps,
 * or the browser denied location) sink to the BOTTOM rather than being treated
 * as zero — otherwise a vendor with no coordinates would rank as "nearest".
 */
export function applySort(list, sortOption, opts = {}) {
  if (!Array.isArray(list) || !sortOption) return list;
  const name = opts.name || (() => '');
  const price = opts.price || (() => null);
  const date = opts.date || ((r) => r?.createdAt);
  const coords = opts.coords || null;

  const byMissingLast = (get, dir) => (a, b) => {
    const x = get(a);
    const y = get(b);
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    return dir * (x - y);
  };

  switch (sortOption) {
    case SORT.A2Z:
      return list.sort((a, b) => String(name(a) || '').localeCompare(String(name(b) || '')));
    case SORT.Z2A:
      return list.sort((a, b) => String(name(b) || '').localeCompare(String(name(a) || '')));
    case SORT.NEW_TO_OLD:
      return list.sort(byMissingLast((r) => num(new Date(date(r)).getTime()), -1));
    case SORT.OLD_TO_NEW:
      return list.sort(byMissingLast((r) => num(new Date(date(r)).getTime()), 1));
    case SORT.PRICE_LOW_HIGH:
      return list.sort(byMissingLast((r) => num(price(r)), 1));
    case SORT.PRICE_HIGH_LOW:
      return list.sort(byMissingLast((r) => num(price(r)), -1));
    case SORT.DISTANCE:
      if (!coords) return list; // no permission yet → leave the order alone
      return list.sort(
        byMissingLast((r) => {
          const g = readGps(r);
          return g ? distanceKm(coords, g) : null;
        }, 1)
      );
    default:
      return list;
  }
}

/**
 * Asks for the browser location only when the user actually picks the
 * distance sort, and remembers it for the rest of the session so a second
 * category doesn't prompt again.
 */
let cachedCoords = null;

export function useUserCoords(enabled) {
  const [coords, setCoords] = useState(cachedCoords);
  useEffect(() => {
    if (!enabled || coords || !navigator?.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        cachedCoords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(cachedCoords);
      },
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  }, [enabled, coords]);
  return coords;
}
